import {
  ActionConfirmationModal,
  Button,
  DataTable,
  Form,
  FormSubmitButton,
  Separator,
  TextAreaFormField,
  TextFormField,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { MultiCategorySelectFormField } from "@/components/ui/FormFields/MultiCategorySelectFormField/MultiCategorySelectFormField";
import { useUser } from "@/components/UserProvider";
import {
  retrieveFirmLeadSourceOptions,
  retrieveFirmLeadSourceMappingTargetsOptions,
  useCreateFirmLeadSource,
  useDeleteFirmLeadSource,
  useUpdateFirmLeadSource,
} from "@/lib/data/react-query/firm/firm-lead-source";
import { retrieveFirmLeadSourceMappingsOptions } from "@/lib/data/react-query/firm/firm-lead-source-mapping";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { baseFastApiLeadSourcesRoute } from "@/lib/data/requests/firm/firm-lead-source";
import { FirmLeadSourceSchema } from "@/lib/data/schemas/firm/FirmSchema";
import { VITE_BASE_API_URL } from "@/lib/data/static/importEnv";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import type { FirmLeadSource, LeadSourceMapping } from "@/lib/types";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { sortBy, toLower } from "lodash";
import { Copy, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import type { z } from "zod";
import { AddOrEditLeadSourceMappingModal } from "./AddOrEditLeadSourceMappingModal";
import { getLeadSourceMappingTableColumns } from "./lead-source-mapping-table-columns";

const baseUrl = VITE_BASE_API_URL;

interface AddOrEditLeadSourceModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

type ConditionalProps =
  | {
      isReadOnly?: boolean;
      editing: true;
      activeLeadSource: Pick<FirmLeadSource, "id" | "dateModified"> &
        Partial<FirmLeadSource>;
      setActiveLeadSourceId: (id: string | null) => void;
    }
  | {
      isReadOnly?: never;
      editing: false;
      activeLeadSource?: never;
      setActiveLeadSourceId?: never;
    };

const formSchema = FirmLeadSourceSchema.pick({
  description: true,
  label: true,
  statusId: true,
});

const defaultFormValues = {
  description: "",
  label: "",
  statusId: "",
};

const FORM_ID = "lead-source-form";

export function AddOrEditLeadSourceModal({
  open,
  setOpen,
  activeLeadSource,
  editing,
  isReadOnly,
  setActiveLeadSourceId,
}: AddOrEditLeadSourceModalProps & ConditionalProps) {
  const { hasActionPermission } = useUser();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isMappingModalOpen, setIsMappingModalOpen] = useState(false);
  const [activeMapping, setActiveMapping] = useState<
    LeadSourceMapping | undefined
  >(undefined);
  const [matterStatusOpen, setMatterStatusOpen] = useState(false);
  const [initialMappings, setInitialMappings] = useState<
    Array<{ key: string; targetId: string }>
  >([]);

  const initialValues: z.infer<typeof formSchema> = editing
    ? {
        ...defaultFormValues,
        ...activeLeadSource,
        statusId: activeLeadSource.statusId ?? "",
      }
    : (defaultFormValues as z.infer<typeof formSchema>);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: initialValues,
  });

  const createLeadSource = useCreateFirmLeadSource();
  const updateLeadSource = useUpdateFirmLeadSource();
  const deleteLeadSource = useDeleteFirmLeadSource();

  const isFormLoading =
    createLeadSource.isPending ||
    updateLeadSource.isPending ||
    deleteLeadSource.isPending;

  const {
    data: matterSettingsData,
    isLoading: isMatterSettingsLoading,
    isError: isMatterSettingsError,
  } = useQuery(retrieveMatterSettingsQueryOptions());
  const matterStatuses = useMemo(
    () => matterSettingsData?.data.statuses ?? [],
    [matterSettingsData],
  );
  const groupedMatterStatuses = useMemo(
    () =>
      sortBy(matterStatuses, (status) => status.category.label).reduce<
        { category: string; items: { value: string; label: string }[] }[]
      >((acc, status) => {
        const category = status.categoryId;
        const item = {
          value: status.id,
          label: status.label,
        };
        const existingCategory = acc.find((c) => c.category === category);
        if (existingCategory) {
          existingCategory.items.push(item);
        } else {
          acc.push({ category, items: [item] });
        }

        return acc;
      }, []),
    [matterStatuses],
  );
  const { data: leadSourcesData } = useQuery(retrieveFirmLeadSourceOptions());
  const { data: mappingTargetsData } = useQuery(
    retrieveFirmLeadSourceMappingTargetsOptions(),
  );
  const leadSources = useMemo(
    () => leadSourcesData?.data ?? [],
    [leadSourcesData],
  );
  const webhookToken =
    editing && activeLeadSource.webhookToken
      ? `${baseUrl}/${baseFastApiLeadSourcesRoute}/webhooks/${activeLeadSource.webhookToken}`
      : undefined;

  const mappingsLeadSourceId = editing ? activeLeadSource.id : "";
  const { data: mappingsData } = useQuery({
    ...retrieveFirmLeadSourceMappingsOptions(mappingsLeadSourceId),
    enabled: editing && !!activeLeadSource.id,
  });

  const canCreate = hasActionPermission(
    ACTION_PERMISSION_KEYS.leadSource,
    ACTION_PERMISSION_VALUES.create,
  );
  const canUpdate = hasActionPermission(
    ACTION_PERMISSION_KEYS.leadSource,
    ACTION_PERMISSION_VALUES.update,
  );
  const mappingColumns = useMemo(() => getLeadSourceMappingTableColumns(), []);

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    if (
      leadSources.some(
        (leadSource) =>
          toLower(leadSource.label) === toLower(data.label) &&
          leadSource.id !== activeLeadSource?.id,
      )
    ) {
      form.setError("label", {
        message: "Lead source label must be unique",
      });
      return;
    }
    if (editing) {
      updateLeadSource.mutate(
        {
          id: activeLeadSource.id,
          dateModified: activeLeadSource.dateModified,
          ...data,
        },
        {
          onSuccess: () => {
            setOpen(false);
          },
        },
      );
    } else {
      createLeadSource.mutate(
        {
          ...data,
          ...(initialMappings.length > 0 ? { mappings: initialMappings } : {}),
        },
        {
          onSuccess: () => {
            setOpen(false);
          },
        },
      );
    }
  };

  const handleDelete = () => {
    if (editing) {
      deleteLeadSource.mutate(
        {
          id: activeLeadSource.id,
        },
        {
          onSuccess: () => {
            setOpen(false);
            setActiveLeadSourceId(null);
          },
        },
      );
    }
  };

  useEffect(() => {
    if (!open) {
      form.reset();
      setIsDeleting(false);
      setIsMappingModalOpen(false);
      setActiveMapping(undefined);
      setMatterStatusOpen(false);
      setInitialMappings([]);
    }
  }, [open, form]);

  const canDelete =
    editing &&
    hasActionPermission(
      ACTION_PERMISSION_KEYS.leadSource,
      ACTION_PERMISSION_VALUES.delete,
    );

  const deleteButton = canDelete ? (
    <div>
      <Button
        type="button"
        variant="destructive"
        onClick={() => {
          setIsDeleting(true);
        }}
      >
        Delete
      </Button>
    </div>
  ) : null;

  return (
    <>
      <Form {...form}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={editing ? "Edit lead source" : "Add lead source"}
          description={
            editing
              ? "Edit the details of this lead source"
              : "Create a new lead source for tracking matter origins"
          }
          isDirty={form.formState.isDirty}
          loading={isFormLoading}
          size="2xl"
          footer={
            <div className="flex w-full flex-wrap items-center justify-between gap-2">
              {deleteButton}
              <div className="flex grow justify-end space-x-2">
                <FormCancelButton
                  label={isReadOnly ? "Close" : "Cancel"}
                  setOpen={setOpen}
                  isDirty={form.formState.isDirty}
                />
                {!isReadOnly && (
                  <FormSubmitButton
                    submitFormId={FORM_ID}
                    loading={isFormLoading}
                    disabled={isFormLoading}
                  />
                )}
              </div>
            </div>
          }
        >
          <form
            id={FORM_ID}
            className="flex min-h-0 grow flex-col overflow-hidden"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <div className="overflow-y-auto">
              <fieldset
                disabled={isReadOnly}
                className={slideoverFieldsetClassName}
              >
                <TextFormField
                  register={form.register("label")}
                  label="Label *"
                />
                <MultiCategorySelectFormField
                  label="Matter status*"
                  items={groupedMatterStatuses}
                  placeholder="Select matter status"
                  emptyMessage="No status found."
                  register={form.register("statusId")}
                  open={matterStatusOpen}
                  setOpen={setMatterStatusOpen}
                  isItemsError={isMatterSettingsError}
                  isItemsLoading={isMatterSettingsLoading}
                />
                <div className="col-span-2">
                  <TextAreaFormField
                    register={form.register("description")}
                    label="Description"
                  />
                </div>
                {webhookToken && (
                  <div className="col-span-2 flex items-center justify-between rounded-md border p-3">
                    <div className="text-muted-foreground truncate text-sm">
                      Copy lead source endpoint
                    </div>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(webhookToken);
                          toast.success("Webhook token copied");
                        } catch {
                          toast.error("Unable to copy token");
                        }
                      }}
                    >
                      <Copy className="mr-2 h-4 w-4" /> Copy
                    </Button>
                  </div>
                )}
              </fieldset>

              {editing && (
                <div className="p-6">
                  <Separator className="my-4" />
                  <div className="mb-3">
                    <h3 className="text-base font-medium">
                      Lead source mappings
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Map incoming keys to contact/matter attributes
                    </p>
                  </div>
                  <DataTable
                    entityLabelPlural="lead source mappings"
                    hideVisiibilityDropdown
                    hideSearchBar
                    tableButtons={
                      canCreate ? (
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => {
                            setActiveMapping(undefined);
                            setIsMappingModalOpen(true);
                          }}
                        >
                          <Plus className="mr-2 h-4 w-4" /> Add mapping
                        </Button>
                      ) : undefined
                    }
                    columns={mappingColumns}
                    data={mappingsData?.data ?? []}
                    onRowClick={(row) => {
                      if (!canUpdate) return;
                      setActiveMapping(row.original);
                      setIsMappingModalOpen(true);
                    }}
                    isLoading={false}
                    isError={false}
                    tableName={tanstackTableNames.leadSourceMappings}
                  />
                </div>
              )}
              {!editing && canCreate && (
                <div className="px-6 pb-6">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setIsMappingModalOpen(true);
                    }}
                  >
                    <Plus className="mr-2 h-4 w-4" /> Add initial mapping
                  </Button>
                </div>
              )}
              {!editing && initialMappings.length > 0 && (
                <div className="p-6 pt-0">
                  <p className="text-muted-foreground text-sm">
                    {initialMappings.length} mapping
                    {initialMappings.length === 1 ? "" : "s"} will be created
                    with this lead source.
                  </p>
                </div>
              )}
            </div>
          </form>
        </AppModal>
      </Form>

      {editing && activeLeadSource.id && (
        <AddOrEditLeadSourceMappingModal
          open={isMappingModalOpen}
          setOpen={setIsMappingModalOpen}
          leadSourceId={activeLeadSource.id}
          {...(activeMapping
            ? { editing: true, activeMapping }
            : { editing: false })}
        />
      )}
      {!editing && (
        <AddOrEditLeadSourceMappingModal
          open={isMappingModalOpen}
          setOpen={setIsMappingModalOpen}
          editing={false}
          onSaveDraft={(mapping) => {
            const target = mappingTargetsData?.data.find(
              ({ id }) => id === mapping.targetId,
            );
            const isDuplicate = initialMappings.some(
              (existing) =>
                existing.key === mapping.key ||
                existing.targetId === mapping.targetId,
            );
            if (isDuplicate) {
              toast.error("Each key and destination can only be mapped once.");
              return;
            }
            if (!target) {
              toast.error("Select a current mapping destination.");
              return;
            }
            setInitialMappings((mappings) => [...mappings, mapping]);
          }}
        />
      )}

      <ActionConfirmationModal
        title="Are you sure you want to delete this lead source?"
        open={isDeleting && !!activeLeadSource}
        setOpen={setIsDeleting}
        afterActionFn={() => {
          if (activeLeadSource) {
            handleDelete();
          }
        }}
      />
    </>
  );
}
